import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssignLeadInput,
  ConvertLeadInput,
  ConvertLeadResponse,
  CreateLeadInput,
  CreateLeadResult,
  DuplicateCandidate,
  LeadCountsResponse,
  LeadResponse,
  LeadSourceOption,
  LeadStatusOption,
  ListLeadsQuery,
  UpdateLeadInput,
} from '@orion/contracts';
import { api, getPage } from '../../lib/api-client.js';

function toQueryString(query: Partial<ListLeadsQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useLeadsQuery(query: Partial<ListLeadsQuery>) {
  return useQuery({
    queryKey: ['leads', query],
    queryFn: () => getPage<LeadResponse[]>(`/leads${toQueryString(query)}`),
    placeholderData: (prev) => prev,
  });
}

export function useLeadCountsQuery() {
  return useQuery({
    queryKey: ['leads', 'counts'],
    queryFn: () => api.get<LeadCountsResponse>('/leads/counts'),
  });
}

export function useLeadStatusOptionsQuery() {
  return useQuery({
    queryKey: ['leads', 'statuses'],
    queryFn: () => api.get<LeadStatusOption[]>('/leads/statuses'),
    staleTime: 5 * 60_000,
  });
}

export function useLeadSourceOptionsQuery() {
  return useQuery({
    queryKey: ['leads', 'sources'],
    queryFn: () => api.get<LeadSourceOption[]>('/leads/sources'),
    staleTime: 5 * 60_000,
  });
}

export function useLeadQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['leads', id],
    queryFn: () => api.get<LeadResponse>(`/leads/${id}`),
    enabled: Boolean(id),
  });
}

export function useDuplicateCheck() {
  return useMutation({
    mutationFn: (input: CreateLeadInput) => api.post<DuplicateCandidate[]>('/leads/duplicate-check', input),
  });
}

/** Posts to `/leads/intake`, not `/leads` directly — the manual form goes
 * through the same AI-qualification gate every other lead-creation path
 * does (see `GatedLeadIntakeService`), so the result isn't always a
 * created Lead. */
export function useCreateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLeadInput) => api.post<CreateLeadResult>('/leads/intake', input),
    onSuccess: (result) => {
      if (result.status === 'created') queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useUpdateLead(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateLeadInput) => api.patch<LeadResponse>(`/leads/${id}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useConvertLead(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ConvertLeadInput) => api.post<ConvertLeadResponse>(`/leads/${id}/convert`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useAssignLead(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignLeadInput) => api.patch<LeadResponse>(`/leads/${id}/assign`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });
}

/** Bulk "Assign" (design/orion-crm-design/rendered-html/LeadsList.html's
 * row-selection toolbar) — there's no dedicated bulk-assign endpoint, so
 * this calls the real per-lead `PATCH :id/assign` once per selected lead.
 * Each call is still a genuine, audited, RBAC-checked update; this just
 * orchestrates it client-side instead of adding a bulk endpoint for what's
 * usually a handful of rows at a time. */
export function useBulkAssignLeads() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, ownerId }: { ids: string[]; ownerId: string }) => {
      await Promise.all(ids.map((id) => api.patch<LeadResponse>(`/leads/${id}/assign`, { ownerId })));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });
}

export function useMergeLead(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (duplicateId: string) => api.post<LeadResponse>(`/leads/${id}/merge`, { duplicateId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });
}

/** Soft delete (`Lead.deletedAt`) — makes the lead disappear from every
 * list/search/count for good; nothing in the UI offers undo. */
export function useDeleteLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/leads/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });
}

/** Bulk delete (AI Lead Review's bulk-action toolbar, `AiLeadReviewPage.tsx`,
 * is the precedent for this client-side-orchestrated shape) — same
 * reasoning as `useBulkAssignLeads`: no dedicated bulk endpoint, so this
 * calls the real per-lead `DELETE :id` once per selected lead. */
export function useBulkDeleteLeads() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => api.delete<void>(`/leads/${id}`)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      return { succeeded: ids.length - failed, failed };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });
}
